import { Delete, KeyboardArrowDown, Sell } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, Menu } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDeviceSignal } from '~/api/device-signals';
import { BulkActionButton, BulkActionMenuItem } from '~/components/bulk-actions';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { focusableDisabledItems } from '~/components/menu-item';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useBulkActionIds } from '~/hooks/unique-id';

export type BulkActionsProps = {
  selectedIds: GridRowSelectionModel;
  canDestroySelectedSignals: boolean;
  onTagSignal: () => void;
};

export const BulkActions = ({
  selectedIds,
  canDestroySelectedSignals,
  onTagSignal,
}: BulkActionsProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { buttonId, menuId } = useBulkActionIds();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [deleteSignal] = useDeleteDeviceSignal();

  if (!selectedIds.length) return null;

  return (
    <>
      <BulkActionButton
        ref={buttonRef}
        disableRipple
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </BulkActionButton>
      <Menu
        id={menuId}
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{
          ...focusableDisabledItems,
          'aria-labelledby': buttonId,
        }}
      >
        <BulkActionMenuItem
          onClick={() => {
            setOpen(false);
            onTagSignal();
          }}
        >
          <ListItemIcon>
            <Sell />
          </ListItemIcon>
          <ListItemText>{t('common.tags.add_tag')}</ListItemText>
        </BulkActionMenuItem>
        <Divider />
        <BulkActionMenuItem
          tooltip={
            !canDestroySelectedSignals
              ? t('pages.device_signals.list.bulk_actions.delete_tooltip')
              : ''
          }
          disabled={!canDestroySelectedSignals}
          onClick={async () => {
            if (!canDestroySelectedSignals) return;
            setOpen(false);
            if (!(await confirmDelete())) return;
            await deleteSignal({
              variables: {
                input: { signalIds: selectedIds.map((x) => +x) },
              },
            });
          }}
        >
          <ListItemIcon>
            <Delete color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>
            {t('common.buttons.delete')}
          </ListItemText>
        </BulkActionMenuItem>
      </Menu>

      <ConfirmDialog
        {...confirmDeleteProps}
        title={t('pages.device_signals.list.dialogs.delete.title')}
        prompt={t('pages.device_signals.list.dialogs.delete.prompt')}
        confirm={t('pages.device_signals.list.dialogs.delete.confirm')}
        deleteConfirm
      />
    </>
  );
};
